/**
 * Structured-data helpers — small factories that produce JSON-LD objects.
 *
 * Kept as pure functions (no side effects) so callers can either hand the
 * result to `useSEO({ jsonLd })` (which manages lifecycle for us) or, when
 * combining multiple schemas, spread it into an array.
 */
import { SITE } from "@/lib/site";

/**
 * Build a BreadcrumbList JSON-LD payload.
 * @param {{name: string, path: string}[]} crumbs — path is relative (e.g. "/shop")
 */
export function breadcrumbLd(crumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.path.startsWith("http") ? c.path : `${SITE.url}${c.path}`,
    })),
  };
}

/**
 * Build an ItemList JSON-LD payload from a product array. Used on `/shop`
 * (and category views) so Google can render the storefront as a native
 * product carousel in search results.
 */
export function productItemListLd({ items, origin, backendUrl, name }) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: name || "Print Shop products",
    numberOfItems: items.length,
    itemListElement: items.slice(0, 50).map((p, i) => {
      // Resolve absolute image URL — relative paths point at the backend
      // (e.g. /api/sinalite/products/122/photo).
      const rawImg = p.owner_photo_url || p.image || null;
      const image = rawImg && rawImg.startsWith("/") ? `${backendUrl}${rawImg}` : rawImg;
      const productUrl = `${origin}/shop/product/${p.id}`;
      return {
        "@type": "ListItem",
        position: i + 1,
        url: productUrl,
        item: {
          "@type": "Product",
          name: (p.name || "").trim(),
          image: image || undefined,
          category: p.category,
          sku: p.sku,
          url: productUrl,
          brand: { "@type": "Brand", name: SITE.name },
        },
      };
    }),
  };
}
